import { MicIcon, Square } from "lucide-react";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Button } from "~/components/ui/button";
import { toast } from "~/hooks/use-toast";
import { cn } from "~/lib/utils";
import {
  SpeechRecognition,
  SpeechRecognitionErrorEvent,
  SpeechRecognitionEvent,
} from "~/types/speechRecognition";
import { trackEvent } from "~/utils/analytics";

interface AudioRecordingButtonProps {
  className?: string;
  onRecordingStateChange?: (isRecording: boolean) => void;
  isProcessingImprovement: boolean;
  isLoading?: boolean;
  onAutoImproveChunk: (rawTranscriptChunk: string) => void;
  onRecordingStop: () => Promise<void>;
  isProcessingQueue: boolean;
  onTextSpeechUpdate: (text: string) => void;
}

export const AudioRecordingButton: FC<AudioRecordingButtonProps> = ({
  className,
  isProcessingImprovement,
  isLoading,
  onRecordingStateChange,
  onAutoImproveChunk,
  onRecordingStop,
  isProcessingQueue,
  onTextSpeechUpdate,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [volume, setVolume] = useState<number>(0);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const transcriptRef = useRef<string>("");
  const interimTranscriptRef = useRef<string>(""); // Store interim results
  const isRecordingRef = useRef<boolean>(false);
  const autoSaveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastAutoSaveTimeRef = useRef<number>(0);
  const speechTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    onRecordingStateChange?.(isRecording);
    isRecordingRef.current = isRecording;
  }, [isRecording, onRecordingStateChange]);

  useEffect(() => {
    return () => {
      onAutoImproveChunk(interimTranscriptRef.current);

      // Clean up recognition
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
          recognitionRef.current = null;
        } catch (error) {
          console.error("Error stopping recognition on unmount:", error);
        }
      }

      // Clean up auto-save interval
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }

      // Clean up speech timeout
      if (speechTimeoutRef.current) {
        clearTimeout(speechTimeoutRef.current);
        speechTimeoutRef.current = null;
      }

      setIsRecording(false);
      setVolume(0);
    };
  }, []);

  const showToastMessage = useCallback((title: string, message: string) => {
    toast({
      variant: "default",
      title: title,
      description: message,
    });
  }, []);

  const saveTranscriptContent = useCallback(() => {
    if (transcriptRef.current.trim()) {
      const chunk = transcriptRef.current.trim();
      onAutoImproveChunk(chunk);
      transcriptRef.current = "";
    }
  }, [onAutoImproveChunk]);

  const startAutoSave = useCallback(() => {
    if (autoSaveIntervalRef.current) {
      clearInterval(autoSaveIntervalRef.current);
    }
    autoSaveIntervalRef.current = setInterval(() => {
      // Send every 5 seconds if user is still recording and has content
      if (isRecordingRef.current) {
        const finalText = transcriptRef.current.trim();

        if (finalText) {
          onAutoImproveChunk(finalText);
          transcriptRef.current = "";
          lastAutoSaveTimeRef.current = Date.now();
        }
      }
    }, 5000);
  }, [onAutoImproveChunk]);

  const stopAutoSave = useCallback(() => {
    if (autoSaveIntervalRef.current) {
      clearInterval(autoSaveIntervalRef.current);
      autoSaveIntervalRef.current = null;
    }

    if (speechTimeoutRef.current) {
      clearTimeout(speechTimeoutRef.current);
      speechTimeoutRef.current = null;
    }
  }, []);

  const requestMicrophonePermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === "NotAllowedError") {
          showToastMessage(
            "Permission Denied",
            "Please allow microphone access to use voice recording."
          );
        } else if (error.name === "NotFoundError") {
          showToastMessage(
            "No Microphone",
            "No microphone found. Please connect a microphone and try again."
          );
        } else {
          showToastMessage(
            "Microphone Error",
            "Unable to access microphone. Please check your settings."
          );
        }
      }
      return false;
    }
  }, [showToastMessage]);

  const initializeSpeechRecognition = useCallback(() => {
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      showToastMessage(
        "Error",
        "Speech recognition is not supported in this browser. Please use Chrome, Safari, or Edge."
      );
      return null;
    }

    const recognition: SpeechRecognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const newVolume = Math.min(0.5 + Math.random() * 0.7, 1);
      setVolume(newVolume);
      let interimTranscript = "";
      let finalTranscript = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + " ";
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript) {
        transcriptRef.current += finalTranscript;
      }

      interimTranscriptRef.current = interimTranscript;
      onTextSpeechUpdate(
        transcriptRef.current.length
          ? transcriptRef.current
          : interimTranscriptRef.current
      );

      setTimeout(() => {
        setVolume(0);
      }, 200);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (isRecording) {
        setIsRecording(false);
      }

      if (event.error === "no-speech") return;

      const messages: Record<string, string> = {
        "not-allowed": "Please allow microphone access and try again.",
        network: "Network error occurred. Please check your connection.",
      };

      showToastMessage(
        "Speech Recognition Error",
        messages[event.error] ?? "An unexpected error occurred."
      );
    };

    recognition.onend = () => {
      if (isRecordingRef.current) {
        setTimeout(() => {
          if (recognitionRef.current && isRecordingRef.current) {
            recognitionRef.current.start();
          }
        }, 100);
      } else {
        stopAutoSave();
        onRecordingStop();
      }
    };

    return recognition;
  }, [showToastMessage, isRecording, stopAutoSave, onRecordingStop]);

  const handleRecordingClick = useCallback(async () => {
    trackEvent({ eventName: "app/notes/record_audio.button/click" });

    if (isRecording) {
      setIsRecording(false);
      isRecordingRef.current = false;

      stopAutoSave();

      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (error) {}
      }
      setVolume(0);
      saveTranscriptContent();
    } else {
      const hasPermission = await requestMicrophonePermission();
      if (!hasPermission) {
        return;
      }

      const recognition = initializeSpeechRecognition();
      if (recognition) {
        recognitionRef.current = recognition;
        setIsRecording(true);
        isRecordingRef.current = true;
        lastAutoSaveTimeRef.current = Date.now();
        try {
          recognition.start();
        } catch (error) {
          showToastMessage(
            "Speech Recognition Error",
            "Failed to start speech recognition. Please try again."
          );
          setIsRecording(false);
          isRecordingRef.current = false;
          return;
        }
        startAutoSave();
      }
    }
  }, [
    isRecording,
    initializeSpeechRecognition,
    requestMicrophonePermission,
    stopAutoSave,
    saveTranscriptContent,
    startAutoSave,
    showToastMessage,
  ]);

  return (
    <div
      className={cn(
        "absolute transition-all duration-1000 ease-in-out",
        className,
        {
          "left-2": !isProcessingImprovement,
          "left-1/2 bottom-1/2 -translate-x-1/2 -translate-y-1/2":
            isProcessingImprovement,
          "opacity-[0.5]": isProcessingImprovement,
        }
      )}
    >
      <div
        className={cn(
          "scale-100 opacity-30 absolute left-0 bottom-0 rounded-full h-full w-full flex items-center justify-center transition-all",
          {
            "bg-red-500": isRecording && volume > 0 && !isProcessingImprovement,
          }
        )}
        style={{
          transform:
            isRecording && volume > 0 ? `scale(${0.7 + volume})` : "scale(1)",
        }}
      ></div>
      <Button
        size="mini"
        onClick={handleRecordingClick}
        variant={isRecording ? "destructive" : "default"}
        disabled={isProcessingImprovement || isLoading || isProcessingQueue}
        className={cn(
          "rounded-full flex items-center justify-center transition-all z-10 relative duration-1000 ease-in-out",
          {
            "h-8 w-8": !isProcessingImprovement,
            "h-[178px] w-[178px]": isProcessingImprovement,
            "bg-[#94BFF1]": isProcessingImprovement,
          }
        )}
        aria-label={
          isProcessingImprovement
            ? "Processing..."
            : isRecording
              ? "Stop recording"
              : "Start recording"
        }
      >
        <div
          className={cn("transition-opacity duration-500", {
            "opacity-100": !isProcessingImprovement,
            "opacity-0": isProcessingImprovement,
          })}
        >
          {isRecording ? (
            <Square fill="white" className="w-4 h-4" />
          ) : (
            <MicIcon className="w-4 h-4" />
          )}
        </div>
      </Button>
    </div>
  );
};
